import getRecurrenceHandler from 'owa-timeformat/lib/utils/recurrence/getRecurrenceHandler';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import {
    isRelativeMonthlyRecurrence,
    isRelativeYearlyRecurrence,
    isMonthlyRecurrence,
    isYearlyRecurrence,
} from 'owa-recurrence-utils/lib/checkRecurrenceTypes';
import createAbsoluteMonthlyRecurrence from 'owa-recurrence-utils/lib/createAbsoluteMonthlyRecurrence';
import createAbsoluteYearlyRecurrence from 'owa-recurrence-utils/lib/createAbsoluteYearlyRecurrence';
import createRelativeMonthlyRecurrence from 'owa-recurrence-utils/lib/createRelativeMonthlyRecurrence';
import createRelativeYearlyRecurrence from 'owa-recurrence-utils/lib/createRelativeYearlyRecurrence';
import getRecurrenceInterval from 'owa-recurrence-utils/lib/getRecurrenceInterval';
import getWeekOrder from 'owa-recurrence-utils/lib/getWeekOrder';
import getRecurrenceEndDate from 'owa-recurrence-utils/lib/getRecurrenceEndDate';
import getRecurrenceDayOfWeekIndex from 'owa-recurrence-utils/lib/getRecurrenceDayOfWeekIndex';
import type { OwaDate } from 'owa-datetime';
import type DayOfWeekIndexType from 'owa-service/lib/contract/DayOfWeekIndexType';

export interface RecurrenceChoiceOption {
    key: string;
    text: string;
    recurrence: RecurrenceType;
}

export default function getRecurrenceChoiceOptions(
    start: OwaDate,
    timeZoneId: string,
    recurrence: RecurrenceType
): { options: RecurrenceChoiceOption[]; selectedKey: string } {
    const startDayOfWeekIndex = getWeekOrder(start); // get from selected pattern
    const isRelative =
        isRelativeMonthlyRecurrence(recurrence) || isRelativeYearlyRecurrence(recurrence);

    let options = [];
    let selectedKey = isRelative ? 'Relative' : 'Absolute';

    const absoluteRecurrence = getAbsoluteRecurrence(start, timeZoneId, recurrence);
    options.push({
        key: 'Absolute',
        text: getRecurrenceHandler(absoluteRecurrence.RecurrencePattern).formatRecurrenceOption(
            absoluteRecurrence.RecurrencePattern
        ),
        recurrence: absoluteRecurrence,
    });

    if (startDayOfWeekIndex == 'Fourth') {
        // When the user has selected the fourth occurrence of a day in a month provide the option
        // of creating the recurrence on the fourth or last occurrence of that day
        if (isRelative) {
            selectedKey =
                getRecurrenceDayOfWeekIndex(recurrence) == 'Fourth'
                    ? 'RelativeFourth'
                    : 'RelativeLast';
        }

        const relativeFourthRecurrence = getRelativeRecurrence(
            start,
            timeZoneId,
            recurrence,
            'Fourth'
        );
        options.push({
            key: 'RelativeFourth',
            text: getRecurrenceHandler(
                relativeFourthRecurrence.RecurrencePattern
            ).formatRecurrenceOption(relativeFourthRecurrence.RecurrencePattern),
            recurrence: relativeFourthRecurrence,
        });

        const relativeLastRecurrence = getRelativeRecurrence(start, timeZoneId, recurrence, 'Last');
        options.push({
            key: 'RelativeLast',
            text: getRecurrenceHandler(
                relativeLastRecurrence.RecurrencePattern
            ).formatRecurrenceOption(relativeLastRecurrence.RecurrencePattern),
            recurrence: relativeLastRecurrence,
        });
    } else {
        // Otherwise provide only one relative option on the day of week index selected:
        // First, Second, Third, or Last if it is the fifth occurrence of the day
        const relativeRecurrence = getRelativeRecurrence(start, timeZoneId, recurrence);

        options.push({
            key: 'Relative',
            text: getRecurrenceHandler(relativeRecurrence.RecurrencePattern).formatRecurrenceOption(
                relativeRecurrence.RecurrencePattern
            ),
            recurrence: relativeRecurrence,
        });
    }

    return { options: options, selectedKey: selectedKey };
}

function getAbsoluteRecurrence(
    start: OwaDate,
    timeZoneId: string,
    recurrence: RecurrenceType
): RecurrenceType {
    const endDate = getRecurrenceEndDate(timeZoneId, recurrence);
    if (isMonthlyRecurrence(recurrence)) {
        return createAbsoluteMonthlyRecurrence(start, getRecurrenceInterval(recurrence), endDate);
    } else if (isYearlyRecurrence(recurrence)) {
        return createAbsoluteYearlyRecurrence(start, endDate);
    } else {
        return null;
    }
}

function getRelativeRecurrence(
    start: OwaDate,
    timeZoneId: string,
    recurrence: RecurrenceType,
    dayOfWeekIndex?: DayOfWeekIndexType
): RecurrenceType {
    const endDate = getRecurrenceEndDate(timeZoneId, recurrence);
    if (isMonthlyRecurrence(recurrence)) {
        return createRelativeMonthlyRecurrence(
            start,
            getRecurrenceInterval(recurrence),
            endDate,
            dayOfWeekIndex
        );
    } else if (isYearlyRecurrence(recurrence)) {
        return createRelativeYearlyRecurrence(start, endDate, dayOfWeekIndex);
    } else {
        return null;
    }
}
